import React, { useCallback } from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import { usePopoverPaginationInfiniteScroll } from "@skydio/relay-toolkit";
import { ComboBox, useDebouncedState } from "@skydio/rivet-ui";

import { useFilteredItemWithMemory } from "components/common/filters/useFilteredItemWithMemory";

import type { FC } from "react";
import type { Key } from "@skydio/rivet-ui";
import type { UserSelect_query$key } from "__generated__/UserSelect_query.graphql";
import type { UserSelectQuery } from "__generated__/UserSelectQuery.graphql";

const usersFragment = graphql`
  fragment UserSelect_query on Query
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    searchTerm: { type: "String" }
  )
  @refetchable(queryName: "UserSelectRefetchQuery") {
    users(first: $count, after: $cursor, searchTerm: $searchTerm, onlySkydioEmployees: true)
      @connection(key: "UserSelect_users") {
      edges {
        node {
          id
          uuid
          email
          organization {
            id
            name
          }
        }
      }
    }
  }
`;

const query = graphql`
  query UserSelectQuery($cursor: String, $count: Int!, $searchTerm: String) {
    ...UserSelect_query @arguments(cursor: $cursor, count: $count, searchTerm: $searchTerm)
  }
`;

const PAGE_SIZE = 20;

interface UserSelectProps {
  selectedUserUuid?: Key | null;
  setSelectedUserUuid?: (selectedUserUuid: Key | null) => void;
}

const UserSelect: FC<UserSelectProps> = (props: UserSelectProps) => {
  const [inputValue, setSearchTerm, { debouncedValue: searchTerm }] = useDebouncedState("");
  const usersQuery = useLazyLoadQuery<UserSelectQuery>(query, {
    count: PAGE_SIZE,
  });

  const paginationQuery = usePaginationFragment<UserSelectQuery, UserSelect_query$key>(
    usersFragment,
    usersQuery
  );

  const [infiniteScrollProps, currentItems] = usePopoverPaginationInfiniteScroll(
    paginationQuery,
    data => data.users,
    PAGE_SIZE,
    { searchTerm }
  );

  const itemProps = useFilteredItemWithMemory({
    currentItems,
    selectedKey: props.selectedUserUuid ?? null,
    getKey: user => user.uuid,
  });

  const handleClear = useCallback(() => {
    setSearchTerm("");
    props.setSelectedUserUuid?.(null);
  }, [setSearchTerm, props]);

  return (
    <ComboBox
      placeholder="Search users"
      inputValue={inputValue}
      onInputChange={setSearchTerm}
      selectedKey={props.selectedUserUuid}
      onSingleSelectionChange={props.setSelectedUserUuid}
      menuTrigger="input"
      onClear={handleClear}
      aria-label="Search users"
      {...itemProps}
      {...infiniteScrollProps}
    >
      {user => (
        <ComboBox.Item key={user.uuid} description={user.uuid} aria-label="user.uuid">
          {`${user.email} (${user?.organization?.name ?? "No organization"})`}
        </ComboBox.Item>
      )}
    </ComboBox>
  );
};

export default UserSelect;
